// Discover Maps Popup Logic
import { getText, getCurrentLanguage } from '../translations/translations.js';
import { clearAllUI, setUIState } from '../utils/clearAll.js';
import { setHeaderButtonActive } from './header-buttons.js';
import { updateUrlForDiscover, parseUrlParams } from '../utils/url-utils.js';
import { updateMapView } from '../map/map-transitions.js';
import { getSpotsByDiscover } from '../utils/spot-queries.js';
import { getLastSelectedAreaSlug, getAreaBySlug } from '../area-selector/index.js';
import { setCardsContext } from '../cards/index.js';
import Popup from '../components/Popup.js';
import { createIconElement } from '../utils/icon.js';
import { preloadMinimalForArea, getMinimalResultsForSpots } from '../utils/cache/index.js';
import { LOGGING } from '/settings.js';

const MAPS_API = '/app_core/api/sqlite/get-maps.php';

let allMaps = [];
let currentPopup = null;
let isTransitioning = false; // Flag to prevent multiple transitions
let mapSelected = false; // Flag to track if a map was just selected

export async function initializeDiscoverBar() {
    // Check if we can reuse an existing popup
    const reusePopup = window.currentPopupInstance && window.currentPopupInstance.element && document.body.contains(window.currentPopupInstance.element);

    clearAllUI({ animate: true, keepPopup: reusePopup, keepSpot: true });
    // Set global UI state: popupOpen true
    setUIState('popupOpen', true);
    // Keep Maps button active while the popup is open
    setHeaderButtonActive('discover');

    // Get current area to fetch maps for that area
    // 1. Try to get from URL first (source of truth)
    const urlParams = parseUrlParams();
    let areaSlug = urlParams.areaSlug;

    // 2. Fallback to localStorage if not in URL
    if (!areaSlug) {
        areaSlug = getLastSelectedAreaSlug();
    }

    let areaId = null;
    let areaName = '';

    if (areaSlug) {
        const area = await getAreaBySlug(areaSlug);
        if (area) {
            areaId = area.id;
            const lang = getCurrentLanguage();
            areaName = area[`name_${lang}`] || area.name_en;
        }
    }

    if (!areaId) {
        if (LOGGING.ENABLED && LOGGING.MENU) console.warn('[Discover] No area selected, cannot fetch maps');
        return;
    }

    // Fetch maps for the current area (use cached fetch to prevent duplicate requests)
    try {
        const { fetchWithCache } = await import('../utils/cache/index.js');
        allMaps = await fetchWithCache(`${MAPS_API}?area_id=${areaId}`);
        if (!Array.isArray(allMaps)) {
            allMaps = [];
        }
    } catch (e) {
        if (LOGGING.ENABLED && LOGGING.MENU) console.error('[Discover] Error fetching maps:', e);
        allMaps = [];
    }

    if (!allMaps.length) {
        if (LOGGING.ENABLED && LOGGING.MENU) console.warn('[Discover] No maps found for area:', areaId);
        return;
    }

    // Preload minimal results for the entire area (for instant marker display)
    // This runs in background, non-blocking
    preloadMinimalForArea(areaId).catch(error => {
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.warn('[Discover] Failed to preload minimal results:', error);
        }
    });

    // Create popup content (all maps, no random selection)
    const content = createMapsGrid();

    // No footer needed (removed refresh button)
    const footer = '';

    // Dynamic Title: Discover [Area]
    const discoverTextRaw = await getText('buttons.discover'); // "DISCOVER"
    // Convert to Title Case (e.g. "Discover")
    const discoverText = discoverTextRaw.charAt(0).toUpperCase() + discoverTextRaw.slice(1).toLowerCase();
    const title = `${discoverText} ${areaName}`;

    if (reusePopup) {
        // Reuse existing popup
        window.currentPopupInstance.updateContent({ title, content, footer });
        window.currentPopupInstance.updateOnClose(() => {
            currentPopup = null;
            window.currentPopupInstance = null;
            setUIState('popupOpen', false);
            // Only dispatch event if a map was NOT just selected
            if (!mapSelected) {
                window.dispatchEvent(new CustomEvent('discoverPopupClosed'));
            }
            mapSelected = false; // Reset flag
        });
        currentPopup = window.currentPopupInstance;
        // Remove any lingering fade classes
        currentPopup.element.classList.remove('fading-out');
        currentPopup.element.classList.remove('fade-out');
    } else {
        // Create popup
        currentPopup = Popup({
            title,
            content,
            footer,
            size: 'medium',
            onClose: () => {
                currentPopup = null;
                window.currentPopupInstance = null;
                setUIState('popupOpen', false);
                // Only dispatch event if a map was NOT just selected
                if (!mapSelected) {
                    window.dispatchEvent(new CustomEvent('discoverPopupClosed'));
                }
                mapSelected = false; // Reset flag
            }
        });
        window.currentPopupInstance = currentPopup;

        // Add fade-in animation to new popup
        if (currentPopup && currentPopup.element) {
            currentPopup.element.classList.add('fade-in');
        }
    }
}

function createMapsGrid() {
    const container = document.createElement('div');
    container.className = 'discover-capsules-container';

    // Create grid
    const grid = document.createElement('div');
    grid.className = 'discover-capsules-grid';
    container.appendChild(grid);

    // Render all maps (no random selection)
    const lang = getCurrentLanguage ? getCurrentLanguage() : 'en';

    allMaps.forEach((map) => {
        const mapElement = document.createElement('div');
        mapElement.className = 'discover-capsule-square';

        // Icon container
        const iconContainer = document.createElement('div');
        iconContainer.className = 'discover-capsule-icon-container';
        iconContainer.style.background = map.color || '#00acc1';

        const iconId = normalizeIconId(map.icon) || 'map-location-dot-solid';
        const iconEl = createIconElement(iconId, 'discover-capsule-icon-img');
        iconContainer.appendChild(iconEl);

        // Text container
        const textContainer = document.createElement('div');
        textContainer.className = 'discover-capsule-text-container';

        const title = document.createElement('div');
        title.className = 'discover-capsule-title';
        title.textContent = map['name_' + lang] || map.name_en;

        const description = document.createElement('div');
        description.className = 'discover-capsule-description';
        description.textContent = map['description_' + lang] || map.description_en;

        textContainer.appendChild(title);
        textContainer.appendChild(description);

        // Assemble map element
        mapElement.appendChild(iconContainer);
        mapElement.appendChild(textContainer);

        // Click event - use the same handler function
        mapElement.addEventListener('click', createMapClickHandler(map), { once: true });

        grid.appendChild(mapElement);
    });

    return container;
}

// Create a click handler function that prevents multiple transitions
function createMapClickHandler(map) {
    return async () => {
        // Prevent multiple simultaneous transitions
        if (isTransitioning) {
            if (LOGGING.ENABLED && LOGGING.MENU) console.log('Transition already in progress, ignoring click');
            return;
        }

        isTransitioning = true;
        mapSelected = true; // Set flag to indicate a map was selected

        try {
            // Preload map cards FIRST (before spots) since they render first
            const { preloadCardsForMap } = await import('../utils/cache/cards-cache.js');
            if (map && map.id) {
                preloadCardsForMap(map.id);
            }

            // Get area ID and spot IDs for minimal results lookup
            const areaId = map.area || null;
            const spotIds = Array.isArray(map.spot_ids) ? map.spot_ids : [];

            // Prepare map data for cards context (used in both minimal and full flows)
            const lang = getCurrentLanguage();
            const mapData = {
                name: map[`name_${lang}`] || map.name_en,
                description: map[`description_${lang}`] || map.description_en
            };

            // Update URL BEFORE the popup closes, so the button state check works correctly
            updateUrlForDiscover(map);

            // Set cards IMMEDIATELY (not awaited to allow synchronization with map animations)
            // Set cards IMMEDIATELY (not awaited to allow synchronization with other UI animations like map fade)
            setCardsContext('map', {
                mapId: map.id,
                areaId: map.area || map.area_id, // Pass areaId for label resolution
                mapData
            }, true);

            // Try to get cached minimal results for instant marker display
            let minimalSpots = [];
            if (areaId && spotIds.length > 0) {
                minimalSpots = getMinimalResultsForSpots(areaId, spotIds);

                if (minimalSpots.length > 0) {
                    // Convert minimal data to spot format for displaySpots
                    const minimalSpotsFormatted = minimalSpots.map(spot => ({
                        ...spot,
                        _isMinimal: true
                    }));

                    if (LOGGING.ENABLED && LOGGING.CACHE) {
                        console.log(`[Discover] Using cached minimal results: ${minimalSpotsFormatted.length} spots`);
                    }

                    // Display markers immediately with minimal data
                    await updateMapView({
                        spotsToDisplay: minimalSpotsFormatted,
                        searchedTags: [],
                        popupsToFade: [currentPopup]
                    });
                }
            }

            // Load full spot data in background (this will replace minimal markers)
            const spots = await getSpotsByDiscover(map);

            // Update map with full data (only if we had minimal data, otherwise already displayed)
            // Cards already set above, no need to set again
            if (minimalSpots.length > 0 && spots.length > 0) {
                await updateMapView({
                    spotsToDisplay: spots,
                    searchedTags: [],
                    popupsToFade: [],
                    shouldAnimate: false // Don't animate since markers are already visible
                });
            } else if (minimalSpots.length === 0) {
                // No minimal data, use full data (normal flow)
                await updateMapView({
                    spotsToDisplay: spots,
                    searchedTags: [],
                    popupsToFade: [currentPopup] // Pass the popup to fade out with the map
                });
            }

            // Clear popup reference after transition
            currentPopup = null;
            if (window.currentPopupInstance === currentPopup) {
                window.currentPopupInstance = null;
            }

            // Dispatch event
            document.dispatchEvent(new CustomEvent('discoverMapSelected', { detail: map }));

        } catch (error) {
            if (LOGGING.ENABLED && LOGGING.MENU) console.error('Error fetching or displaying spots:', error);
        } finally {
            // Reset transition flag
            isTransitioning = false;
        }
    };
}

function normalizeIconId(icon) {
    if (!icon) return null;
    const base = icon.toString().split('/').pop();
    return base.replace(/\.svg$/i, '');
}
